import '../styles/main.css';
import { useState, useEffect } from 'react';
import infoIcon from '../styles/info.png';
import cityMap from '../data/cityMap.json';

export default function UserForm({ onSubmit, isLoading, language = "en" }) {
  const [birthDate, setBirthDate] = useState("");
  const [amPm, setAmPm] = useState("AM");
  const [birthHour, setBirthHour] = useState("");
  const [birthMinute, setBirthMinute] = useState("");
  const [error, setError] = useState("");
  const [locationData] = useState(cityMap);
  const [country, setCountry] = useState("");
  const [province, setProvince] = useState("");
  const [city, setCity] = useState("");
  const [mbti, setMbti] = useState("");
  const [isMbtiHelpOpen, setIsMbtiHelpOpen] = useState(false);
  const [gender, setGender] = useState("male");
  
  // Single state for the combined optional section
  const [isOptionalSectionFolded, setIsOptionalSectionFolded] = useState(true);

  const currentDate = new Date().toISOString().split('T')[0];
  const t = texts[language];
  const isChinese = language === "zh";

  const hourOptions = isChinese
    ? [...Array(24).keys()].map(h => ({
        value: h,
        label: h.toString().padStart(2, "0")
      }))
    : [...Array(12).keys()].map(h => {
        const hour = h === 0 ? 12 : h;
        return { value: hour, label: hour.toString().padStart(2, "0") };
      });

  const minuteOptions = [...Array(60).keys()].map(m => ({
    value: m,
    label: m.toString().padStart(2, "0")
  }));

  // Load saved form data
  useEffect(() => {
    // Load saved form data
    const storedData = sessionStorage.getItem('baziResults');
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        if (parsedData.formData) {
          const formData = parsedData.formData;
          
          // note: language is controlled by parent; we don't override it here
            
            // Set basic form data
            setBirthDate(formData.birthDate || "");
            setBirthHour(formData.birthHour?.toString() || "");
            setBirthMinute(formData.birthMinute?.toString() || "");
            
            // Handle AM/PM for English
            if (formData.language !== "zh" && formData.birthHour !== undefined) {
              const hour = parseInt(formData.birthHour);
              if (hour >= 12) {
                setAmPm("PM");
              } else {
                setAmPm("AM");
              }
            }

          setGender(formData.gender);
          
          // Set location data
          if (formData.country && cityMap[formData.country]) {
            setCountry(formData.country);
            
            // Set province if available
            let savedProvince = "";
            if (formData.city && cityMap[formData.country].cities[formData.city]) {
                savedProvince = cityMap[formData.country].cities[formData.city].province || "";
            }
            setProvince(savedProvince);

            // Use setTimeout to ensure state updates properly
            setTimeout(() => {
              if (formData.city && cityMap[formData.country]?.cities[formData.city]) {
                setCity(formData.city);
              }
            }, 0);
          }

          setMbti(formData.mbti?.toString() || "");
          
          // Set folded state based on saved data
          if (formData.country || formData.city || formData.mbti) {
            setIsOptionalSectionFolded(false);
          }
        }
      } catch (error) {
        console.error("Error loading saved form data:", error);
      }
    } else {
      // Set default country and city if no saved data
      const defaultCountry = Object.keys(cityMap)[0];
      setCountry(defaultCountry);
      const firstCity = Object.keys(cityMap[defaultCountry]?.cities || {})[0] || "";
      setCity(firstCity);
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const yearStr = birthDate.split('-')[0];
    const yearNum = Number(yearStr);
    const currentYear = new Date().getFullYear();

    if (
      !yearStr ||
      yearStr.length !== 4 ||
      isNaN(yearNum) ||
      yearNum < 1900 ||
      yearNum > currentYear
    ) {
      setError(t.errorYear + currentYear + ")");
      return;
    }

    const hour = Number(birthHour);
    const minute = Number(birthMinute);

    if (
      isNaN(hour) || hour < 0 || hour > 23 ||
      isNaN(minute) || minute < 0 || minute > 59
    ) {
      setError(t.errorHour);
      return;
    }

    let finalHour = hour;
    if (!isChinese) {
      if (amPm === "PM" && hour < 12) finalHour += 12;
      if (amPm === "AM" && hour === 12) finalHour = 0;
    }
    
    // Only validate location if section is unfolded
    let longitude = null;
    let timeZone = null;
    if (!isOptionalSectionFolded && country && city) {
      const cityData = locationData[country]?.cities[city];
      longitude = cityData?.longitude || null;
      timeZone = cityData?.iana_timezone || null;
    }

    setError("");
    
    // Save form data for back navigation
    const formData = {
      birthDate: birthDate,
      birthHour: finalHour,
      birthMinute: minute,
      longitude,
      timeZone,
      language,
      gender,
      country: isOptionalSectionFolded ? "" : country,
      city: isOptionalSectionFolded ? "" : city,
      mbti: isOptionalSectionFolded ? "" : mbti,
    };

    onSubmit(formData);
  };

  const getProvincesForCountry = (countryName) => {
    if (!locationData[countryName] || !locationData[countryName].cities) return [];
    const cities = locationData[countryName].cities;
    const provinces = new Set();
    Object.values(cities).forEach(city => {
      if (city.province) {
        provinces.add(city.province);
      }
    });
    return Array.from(provinces).sort();
  };

  const getProvinceDisplayName = (countryName, provinceName) => {
    if (language !== "zh") return provinceName;
    
    if (!locationData[countryName] || !locationData[countryName].cities) return provinceName;
    
    const cities = locationData[countryName].cities;
    for (const cityKey in cities) {
        if (cities[cityKey].province === provinceName) {
            return cities[cityKey].province_zh || provinceName;
        }
    }
    return provinceName;
  };

  const getCitiesForCountry = (countryName, provinceName) => {
    if (!locationData[countryName] || !locationData[countryName].cities) return [];
    const allCities = Object.keys(locationData[countryName].cities);
    
    const provinces = getProvincesForCountry(countryName);
    if (provinces.length > 0) {
        if (!provinceName) return []; // Force province selection if provinces exist
        return allCities.filter(cityName => 
            locationData[countryName].cities[cityName].province === provinceName
        ).sort();
    }

    return allCities.sort();
  };

  const getCityChineseName = (countryName, cityName) => {
    if (!locationData[countryName] || !locationData[countryName].cities || !locationData[countryName].cities[cityName]) {
      return cityName;
    }
    return locationData[countryName].cities[cityName].city_name_zh || cityName;
  };

  const getCityDisplayName = (countryName, cityName) => {
    if (language === "zh") {
      return getCityChineseName(countryName, cityName);
    }
    return cityName;
  };

  return (
    <form onSubmit={handleSubmit} className="card">
      

      <h1>{t.title}</h1>

      <label htmlFor="birthDate">
        {t.label}
      </label>
      <input
        type="date"
        value={birthDate}
        onChange={e => setBirthDate(e.target.value)}
        placeholder={t.placeholderDate}
        min="1900-01-01"
        max={currentDate}
        required
      />
      
      <div className="time-picker">
        <select value={birthHour} onChange={e => setBirthHour(e.target.value)} style={{flex:1, textAlign:'center'}}>
          <option value="">{isChinese ? "时" : "HH"}</option>
          {hourOptions.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <span style={{ alignSelf: 'center', marginBottom: '20px' }}>:</span>

        <select value={birthMinute} onChange={e => setBirthMinute(e.target.value)} style={{flex:1, textAlign:'center'}}>
          <option value="">{isChinese ? "分" : "MM"}</option>
          {minuteOptions.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {!isChinese && (
          <select value={amPm} onChange={e => setAmPm(e.target.value)} style={{flex:1, textAlign:'center'}}>
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        )}
      </div>

      <div className="gender-toggle-container">
        <label className="gender-label">{t.labelGender}</label>
        <div className={`segmented-toggle ${gender}`}>
          <div className="highlight" />
          <button
            type="button"
            className={gender === "male" ? "active" : ""}
            onClick={() => setGender("male")}
          >
            {t.male}
          </button>
          <button
            type="button"
            className={gender === "female" ? "active" : ""}
            onClick={() => setGender("female")}
          >
            {t.female}
          </button>
        </div>
      </div>

      {/* Combined Optional Section Toggle */}
      <div 
        className={`section-toggle ${isOptionalSectionFolded ? 'folded' : 'unfolded'}`}
        onClick={() => setIsOptionalSectionFolded(!isOptionalSectionFolded)}
      >
        <span className="toggle-arrow">▶</span>
        <span className="toggle-label">
          {isOptionalSectionFolded ? t.showOptional : t.hideOptional}
        </span>
      </div>

      {!isOptionalSectionFolded && (
        <>
          {/* Location Section */}
          <label2 htmlFor="birthPlace">
            {t.label2}
          </label2>

          <div className="location-picker">
            <select
              value={country}
              onChange={(e) => {
                const newCountry = e.target.value;
                setCountry(newCountry);
                setProvince("");
                setCity("");
                
                const provinces = getProvincesForCountry(newCountry);
                if (provinces.length === 0) {
                    const cities = getCitiesForCountry(newCountry, "");
                    if (cities.length > 0) setCity(cities[0]);
                }
              }}
            >
              <option value="">{language === "zh" ? "请选择国家" : "Select Country"}</option>
              {Object.keys(locationData).map((c) => (
                <option key={c} value={c}>
                  {language === "zh" ? locationData[c].country_name_zh : c}
                </option>
              ))}
            </select>

            {country && getProvincesForCountry(country).length > 0 && (
                <select 
                    value={province} 
                    onChange={(e) => {
                        const newProvince = e.target.value;
                        setProvince(newProvince);
                        const cities = getCitiesForCountry(country, newProvince);
                        if (cities.length > 0) setCity(cities[0]);
                        else setCity("");
                    }}
                >
                    <option value="">{language === "zh" ? "请选择省份/州" : "Select Province/State"}</option>
                    {getProvincesForCountry(country).map(p => (
                        <option key={p} value={p}>{getProvinceDisplayName(country, p)}</option>
                    ))}
                </select>
            )}

            {country && (getProvincesForCountry(country).length === 0 || province) && (
              <select value={city} onChange={e => setCity(e.target.value)}>
                <option value="">{language === "zh" ? "请选择城市" : "Select City"}</option>
                {getCitiesForCountry(country, province).map((ct) => (
                  <option key={ct} value={ct}>
                    {getCityDisplayName(country, ct)}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* MBTI Section */}
          <div className="label-with-help">
            <label2 htmlFor="mbti">
              {language === "zh" ? "选择你的MBTI类型：" : "Select Your MBTI Type:"}
            </label2>
            <img 
              src={infoIcon} 
              alt="Help" 
              className="help-icon"
              onClick={(e) => {
                e.stopPropagation();
                setIsMbtiHelpOpen(true);
              }}
            />
          </div>

          <div className="location-picker">
            <select
              id="mbti"
              value={mbti}
              onChange={e => setMbti(e.target.value)}
            >
              <option value="">{language === "zh" ? "请选择MBTI类型" : "Select MBTI Type"}</option>
              <option value="ENFJ">ENFJ</option>
              <option value="ENFP">ENFP</option>
              <option value="ENTJ">ENTJ</option>
              <option value="ENTP">ENTP</option>
              <option value="ESFJ">ESFJ</option>
              <option value="ESFP">ESFP</option>
              <option value="ESTJ">ESTJ</option>
              <option value="ESTP">ESTP</option>
              
              <option value="INFJ">INFJ</option>
              <option value="INFP">INFP</option>
              <option value="INTJ">INTJ</option>
              <option value="INTP">INTP</option>
              <option value="ISFJ">ISFJ</option>
              <option value="ISFP">ISFP</option>
              <option value="ISTJ">ISTJ</option>
              <option value="ISTP">ISTP</option>
            </select>
          </div>
        </>
      )}
      
      {/* MBTI Help Dialog */}
      {isMbtiHelpOpen && (
        <div 
          className="modal-overlay"
          onClick={() => setIsMbtiHelpOpen(false)}
        >
          <div 
            className="modal-content"
            onClick={e => e.stopPropagation()}
          >
            <h3>
              {language === "zh" ? "不知道你的 MBTI 类型？" : "Don’t know your MBTI type?"}
            </h3>
            <p>
              {language === "zh" 
                ? "你可以前往 16Personalities 网站免费测试：" 
                : "You can take a free test on 16Personalities:"}
            </p>
            <a 
              href={language === "zh" ? "https://www.16personalities.com/ch" : "https://www.16personalities.com/"}
              target="_blank"
              rel="noopener noreferrer"
              className="help-link"
            >
              {language === "zh" ? "前往 16Personalities（中文）" : "Go to 16Personalities"}
            </a>
            <button 
              className="close-button"
              onClick={() => setIsMbtiHelpOpen(false)}
            >
              {language === "zh" ? "关闭" : "Close"}
            </button>
          </div>
        </div>
      )}

      <button type="submit" disabled={isLoading}>
        {isLoading ? t.loading : t.button}
      </button>

      {error && <div className="result">{error}</div>}
    </form>
  );
}

const texts = {
  en: {
    title: "🔮 Desti Fortune Teller",
    label: "Enter Your Birth Time:",
    label2: "Select Your Birth Place:",
    placeholderDate: "Birthdate",
    placeholderHour: "Hour of Birth",
    button: "Reveal My Fortune",
    loading: "Consulting the Book of Changes...",
    errorYear: `Please enter a valid 4-digit year between 1900 and `,
    errorHour: "Please enter a valid hour and minute.",
    labelGender: "Gender at Birth:",
    male: "Male",
    female: "Female",
    showOptional: "Add Optional Details for More Accuracy",
    hideOptional: "Hide Optional Details"
  },
  zh: {
    title: "🔮 Desti 命理占卜师",
    label: "请输入你的出生时间：",
    label2: "请选择你的出生地：",
    placeholderDate: "出生日期",
    placeholderHour: "出生小时（0–23）",
    button: "占卜我的命理",
    loading: "正在请教《易经》...",
    errorYear: `请输入有效的4位出生年份（1900–`,
    errorHour: "请输入有效的小时和分钟。",
    labelGender: "出生性别：",
    male: "男",
    female: "女",
    showOptional: "添加可选信息以获得更准确的结果",
    hideOptional: "隐藏可选信息"
  }
};